import { EncryptionKey, UserInfo } from "@/api/types/user";

/**
 * 标签页认证数据结构
 */
export interface TabAuthData {
  accessToken?: string;
  userId?: string;
  tenantId?: string;
  organizationCode?: string;
  userInfo?: UserInfo;
  encryptionKey?: EncryptionKey;
  loginTime?: string;
  tabId?: string;
}

/**
 * 存储配置枚举
 */
enum StorageType {
  SESSION = 'session', // 仅sessionStorage - 标签页独立，刷新丢失
  LOCAL = 'local',     // 仅localStorage - 持久化，标签页共享
  HYBRID = 'hybrid'    // 混合模式 - 持久化 + 标签页隔离
}

/**
 * 标签页状态管理器
 * 提供浏览器多标签页独立认证数据的统一管理
 * 替代 Electron 的多窗口管理系统
 */
class TabStateManager {
  private tabId: string;
  private storageKey: string = 'tab_auth_data';
  private globalStorageKey: string = 'ping_im_auth_data'; // 全局持久化key
  private broadcastChannel: BroadcastChannel;
  private syncInterval: number | null = null;
  private storageType: StorageType = StorageType.HYBRID; // 默认使用混合模式

  constructor(storageType: StorageType = StorageType.HYBRID) {
    this.storageType = storageType;
    
    // 生成稳定的标签页ID（基于会话）
    this.tabId = this.getOrCreateTabId();
    
    // 创建广播通道用于标签页间通信
    this.broadcastChannel = new BroadcastChannel('ping_im_tabs');
    
    this.initialize();
  }

  /**
   * 获取或创建稳定的标签页ID
   * 优先从sessionStorage获取，不存在则生成新的
   */
  private getOrCreateTabId(): string {
    const sessionTabKey = 'ping_im_current_tab_id';
    let tabId = sessionStorage.getItem(sessionTabKey);
    
    if (!tabId) {
      tabId = `tab_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      sessionStorage.setItem(sessionTabKey, tabId);
    }
    
    return tabId;
  }

  private initialize() {
    // 监听页面卸载，清理当前标签页数据
    window.addEventListener('beforeunload', () => {
      this.cleanup();
    });

    // 监听来自其他标签页的消息
    this.broadcastChannel.addEventListener('message', (event) => {
      this.handleBroadcastMessage(event);
    });

    // 设置定期同步机制
    this.setupPeriodicSync();

    console.log('TabStateManager: 初始化完成，标签页ID:', this.tabId);
  }

  private setupPeriodicSync() {
    // 每30秒同步一次状态，确保数据一致性
    this.syncInterval = window.setInterval(() => {
      this.syncTabState();
    }, 30000);
  }

  private syncTabState() {
    // 向其他标签页广播当前状态
    this.broadcastChannel.postMessage({
      type: 'tab_sync',
      tabId: this.tabId,
      timestamp: Date.now(),
      authData: this.getAuthData()
    });
  }

  private handleBroadcastMessage(event: MessageEvent) {
    const { type, tabId, data } = event.data;

    // 忽略来自自己的消息
    if (tabId === this.tabId) return;

    switch (type) {
      case 'tab_sync':
        // 处理其他标签页的状态同步
        console.log('TabStateManager: 收到标签页同步消息:', tabId);
        break;
      
      case 'auth_changed':
        // 处理认证状态变化（可选：实现统一登录/登出）
        console.log('TabStateManager: 收到认证变化消息:', data);
        break;
      
      case 'tab_message':
        // 处理标签页间的自定义消息
        console.log('TabStateManager: 收到标签页消息:', data);
        break;
    }
  }

  private cleanup() {
    if (this.syncInterval) {
      clearInterval(this.syncInterval);
    }
    
    // 广播标签页关闭消息
    this.broadcastChannel.postMessage({
      type: 'tab_closed',
      tabId: this.tabId,
      timestamp: Date.now()
    });
    
    this.broadcastChannel.close();
  }

  // 获取标签页专用的存储key
  private getTabStorageKey(): string {
    return `${this.tabId}_${this.storageKey}`;
  }

  // 获取完整的认证数据
  public getAuthData(): TabAuthData {
    try {
      let authData: any = {};
      
      switch (this.storageType) {
        case StorageType.SESSION:
          // 纯session模式：仅从sessionStorage读取
          const sessionKey = this.getTabStorageKey();
          const sessionData = sessionStorage.getItem(sessionKey);
          authData = sessionData ? JSON.parse(sessionData) : {};
          break;
          
        case StorageType.LOCAL:
          // 纯local模式：仅从localStorage读取
          const localData = localStorage.getItem(this.globalStorageKey);
          authData = localData ? JSON.parse(localData) : {};
          break;
          
        case StorageType.HYBRID:
        default:
          // 混合模式：优先session，fallback到local
          const hybridSessionKey = this.getTabStorageKey();
          let hybridSessionData = sessionStorage.getItem(hybridSessionKey);
          
          if (!hybridSessionData) {
            // session中没有数据，尝试从localStorage恢复
            const hybridLocalData = localStorage.getItem(this.globalStorageKey);
            if (hybridLocalData) {
              authData = JSON.parse(hybridLocalData);
              // 恢复到session中，确保后续访问的一致性
              sessionStorage.setItem(hybridSessionKey, hybridLocalData);
              console.log('TabStateManager: 从localStorage恢复认证数据到session');
            }
          } else {
            authData = JSON.parse(hybridSessionData);
          }
          break;
      }
      
      return { ...authData, tabId: this.tabId };
    } catch (error) {
      console.error('TabStateManager: 解析认证数据失败:', error);
      return { tabId: this.tabId };
    }
  }

  // 设置完整的认证数据
  public setAuthData(data: Partial<TabAuthData>): void {
    try {
      const currentData = this.getAuthData();
      const newData = { ...currentData, ...data, tabId: this.tabId };
      const serializedData = JSON.stringify(newData);
      
      switch (this.storageType) {
        case StorageType.SESSION:
          // 纯session模式：仅保存到sessionStorage
          const sessionKey = this.getTabStorageKey();
          sessionStorage.setItem(sessionKey, serializedData);
          break;
          
        case StorageType.LOCAL:
          // 纯local模式：仅保存到localStorage
          localStorage.setItem(this.globalStorageKey, serializedData);
          break;
          
        case StorageType.HYBRID:
        default:
          // 混合模式：同时保存到两个存储
          const hybridSessionKey = this.getTabStorageKey();
          sessionStorage.setItem(hybridSessionKey, serializedData);
          localStorage.setItem(this.globalStorageKey, serializedData);
          break;
      }
      
      // 广播认证数据变化
      this.broadcastChannel.postMessage({
        type: 'auth_changed',
        tabId: this.tabId,
        data: newData,
        timestamp: Date.now()
      });
    } catch (error) {
      console.error('TabStateManager: 保存认证数据失败:', error);
    }
  }

  // 清除所有认证信息
  public clearAuth(): void {
    try {
      switch (this.storageType) {
        case StorageType.SESSION:
          // 纯session模式：仅清除sessionStorage
          const sessionKey = this.getTabStorageKey();
          sessionStorage.removeItem(sessionKey);
          break;
          
        case StorageType.LOCAL:
          // 纯local模式：仅清除localStorage
          localStorage.removeItem(this.globalStorageKey);
          break;
          
        case StorageType.HYBRID:
        default:
          // 混合模式：清除两个存储
          const hybridSessionKey = this.getTabStorageKey();
          sessionStorage.removeItem(hybridSessionKey);
          localStorage.removeItem(this.globalStorageKey);
          break;
      }
      
      // 广播认证清除消息
      this.broadcastChannel.postMessage({
        type: 'auth_cleared',
        tabId: this.tabId,
        timestamp: Date.now()
      });
    } catch (error) {
      console.error('TabStateManager: 清除认证数据失败:', error);
    }
  }

  // === 便捷的访问方法 ===

  // 获取标签页ID
  public getTabId(): string {
    return this.tabId;
  }

  // 获取access token
  public getAccessToken(): string | null {
    return this.getAuthData().accessToken || null;
  }

  // 设置access token
  public setAccessToken(token: string): void {
    this.setAuthData({ accessToken: token });
  }

  // 获取用户ID
  public getUserId(): string | null {
    return this.getAuthData().userId || null;
  }

  // 设置用户ID
  public setUserId(userId: string): void {
    this.setAuthData({ userId });
  }

  // 获取租户ID
  public getTenantId(): string | null {
    return this.getAuthData().tenantId || null;
  }

  // 设置租户ID
  public setTenantId(tenantId: string): void {
    this.setAuthData({ tenantId });
  }

  // 获取组织代码
  public getOrganizationCode(): string | null {
    return this.getAuthData().organizationCode || null;
  }

  // 设置组织代码
  public setOrganizationCode(code: string): void {
    this.setAuthData({ organizationCode: code });
  }

  // 获取用户信息
  public getUserInfo(): TabAuthData['userInfo'] | null {
    return this.getAuthData().userInfo || null;
  }

  // 设置用户信息
  public setUserInfo(userInfo: TabAuthData['userInfo']): void {
    this.setAuthData({ userInfo });
  }

  // 获取加密密钥
  public getEncryptionKey(): EncryptionKey | null {
    return this.getAuthData().encryptionKey || null;
  }

  // 设置加密密钥
  public setEncryptionKey(encryptionKey: EncryptionKey): void {
    this.setAuthData({ encryptionKey });
  }

  // 批量设置登录数据
  public setLoginData(data: {
    accessToken: string;
    userId: string;
    tenantId?: string;
    userInfo?: TabAuthData['userInfo'];
    encryptionKey?: EncryptionKey;
  }): void {
    this.setAuthData({
      ...data,
      loginTime: new Date().toISOString()
    });
  }

  // 检查是否已认证
  public isAuthenticated(): boolean {
    return !!this.getAccessToken();
  }

  // 广播消息到其他标签页
  public broadcastToOtherTabs(message: any): void {
    this.broadcastChannel.postMessage({
      type: 'tab_message',
      tabId: this.tabId,
      data: message,
      timestamp: Date.now()
    });
  }

  // 监听来自其他标签页的消息
  public onTabMessage(callback: (data: any, fromTabId: string) => void): void {
    const handler = (event: MessageEvent) => {
      const { type, tabId, data } = event.data;
      if (type === 'tab_message' && tabId !== this.tabId) {
        callback(data, tabId);
      }
    };
    
    this.broadcastChannel.addEventListener('message', handler);
  }

  // 监听认证状态变化
  public onAuthChanged(callback: (authData: TabAuthData, fromTabId: string) => void): void {
    const handler = (event: MessageEvent) => {
      const { type, tabId, data } = event.data;
      if (type === 'auth_changed' && tabId !== this.tabId) {
        callback(data, tabId);
      }
    };
    
    this.broadcastChannel.addEventListener('message', handler);
  }

  // 获取所有活跃标签页的数量（估算）
  public getActiveTabsCount(): Promise<number> {
    return new Promise((resolve) => {
      const responses: string[] = [];
      const timeout = 1000; // 1秒超时
      
      const handler = (event: MessageEvent) => {
        const { type, tabId } = event.data;
        if (type === 'ping_response' && tabId !== this.tabId) {
          responses.push(tabId);
        }
      };
      
      this.broadcastChannel.addEventListener('message', handler);
      
      // 发送ping消息
      this.broadcastChannel.postMessage({
        type: 'ping_request',
        tabId: this.tabId,
        timestamp: Date.now()
      });
      
      // 设置响应ping的处理器
      const pingHandler = (event: MessageEvent) => {
        const { type, tabId } = event.data;
        if (type === 'ping_request' && tabId !== this.tabId) {
          this.broadcastChannel.postMessage({
            type: 'ping_response',
            tabId: this.tabId,
            timestamp: Date.now()
          });
        }
      };
      
      this.broadcastChannel.addEventListener('message', pingHandler);
      
      setTimeout(() => {
        this.broadcastChannel.removeEventListener('message', handler);
        this.broadcastChannel.removeEventListener('message', pingHandler);
        resolve(responses.length + 1); // +1 包括当前标签页
      }, timeout);
    });
  }

  /**
   * 设置存储模式
   * @param type 存储类型
   */
  public setStorageType(type: StorageType): void {
    this.storageType = type;
    console.log(`TabStateManager: 存储模式已切换为 ${type}`);
  }

  /**
   * 清除所有存储（包括localStorage和sessionStorage）
   * 用于完全登出或重置状态
   */
  public clearAllStorage(): void {
    try {
      // 清除当前标签页的session数据
      const sessionKey = this.getTabStorageKey();
      sessionStorage.removeItem(sessionKey);
      
      // 清除全局localStorage数据
      localStorage.removeItem(this.globalStorageKey);
      
      // 清除标签页ID
      sessionStorage.removeItem('ping_im_current_tab_id');
      
      console.log('TabStateManager: 已清除所有存储数据');
      
      // 广播全局清除消息
      this.broadcastChannel.postMessage({
        type: 'all_storage_cleared',
        tabId: this.tabId,
        timestamp: Date.now()
      });
    } catch (error) {
      console.error('TabStateManager: 清除所有存储失败:', error);
    }
  }
}

// 创建全局单例（使用混合存储模式）
export const tabStateManager = new TabStateManager(StorageType.HYBRID);

// 导出存储类型枚举
export { StorageType };

// 导出类型以供其他地方使用
export type { TabStateManager };
